import { useEffect, useState } from 'react';

import { DownloadOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Input,
  Popover,
  Row,
  Table,
  TableColumnType,
  Tabs,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import numeral from 'numeral';

import { useDownloadPayments } from '@/hooks/useDownloadPayments';
import {
  Payment,
  useQueryPaymentsByInvoice,
  useQueryPaymentsByPatient,
} from '@/hooks/useQueryPayments';
import useGlobalConfig from '@/store/global';

const { Title } = Typography;

const columns: TableColumnType<any>[] = [
  {
    title: 'ID',
    dataIndex: 'payable_id',
    align: 'center',
  },
  {
    title: 'Invoice Number',
    dataIndex: 'invoice_number',
    render: (_, record) => <span>{record.invoice_number || 'N/A'}</span>,
  },
  {
    title: 'Benefit Type',
    dataIndex: 'payable_type',
    render: (_, record) => {
      return record.payable_type === 'AssistanceRequest' ? 'Medical' : 'Pharmacy';
    },
  },
  {
    title: 'First Name',
    dataIndex: 'patient_first_name',
  },
  {
    title: 'Last Name',
    dataIndex: 'patient_last_name',
  },
  {
    title: 'Date of Birth',
    dataIndex: 'date_of_birth',
    render: (_, record) => (
      <span>{record.date_of_birth ? dayjs(record.date_of_birth).format('MM-DD-YYYY') : 'N/A'}</span>
    ),
  },
  {
    title: 'Date of Service',
    dataIndex: 'date_of_service',
    render: (_, record) => (
      <span>
        {record.date_of_service ? dayjs(record.date_of_service).format('MM-DD-YYYY') : 'N/A'}
      </span>
    ),
  },
  {
    title: 'Amount Paid',
    dataIndex: 'amount_paid',
    render: (_, record) => <span>{numeral(record.amount_paid).format('$0,0.00')}</span>,
  },
  {
    title: 'Check Written',
    dataIndex: 'date_check_written',
    render: (_, record) => (
      <span>
        {record.date_check_written ? dayjs(record.date_check_written).format('MM-DD-YYYY') : 'N/A'}
      </span>
    ),
  },
  {
    title: 'Invoice Total',
    dataIndex: 'check_totals',
    render: (_, record) => <span>{numeral(record.check_totals).format('$0,0.00')}</span>,
  },
  {
    title: 'Method of Payment',
    dataIndex: 'payment_method',
    align: 'center',
  },
  {
    title: 'Business Name',
    dataIndex: 'business_name',
  },
  {
    title: 'Program Name',
    dataIndex: 'program_name',
    align: 'center',
    render: (_, record) => (
      <span>{record.program_name.charAt(0).toUpperCase() + record.program_name.slice(1)}</span>
    ),
  },
  {
    title: 'Mailing Address',
    dataIndex: 'mailing_address',
    render: (_, record) => (
      <span>
        {record.reimbursement_address ? record.reimbursement_address : record.business_address}
      </span>
    ),
  },
  {
    title: 'Line2',
    dataIndex: 'address_line_2',
    render: (_, record) => (
      <span>
        {record.reimbursement_address_line2
          ? record.reimbursement_address_line2
          : record.business_address_line_2}
      </span>
    ),
  },
  {
    title: 'City',
    dataIndex: 'city',
    render: (_, record) => (
      <span>
        {record.reimbursement_address_city
          ? record.reimbursement_address_city
          : record.business_city}
      </span>
    ),
  },
  {
    title: 'State',
    dataIndex: 'state',
    render: (_, record) => (
      <span>
        {record.reimbursement_address_state
          ? record.reimbursement_address_state
          : record.business_state}
      </span>
    ),
  },
  {
    title: 'Zip',
    dataIndex: 'zip',
    render: (_, record) => (
      <span>
        {record.reimbursement_address_zip ? record.reimbursement_address_zip : record.business_zip}
      </span>
    ),
  },
];

const InvoiceNumberPopoverContent = () => (
  <Tabs>
    <Tabs.TabPane tab="Check Payment" key="1">
      <img src="/images/check-payment.png" className="w-full md:w-[30vw]" />
    </Tabs.TabPane>
    <Tabs.TabPane tab="ACH Payment" key="2">
      <img src="/images/ach-payment.png" className="w-full md:w-[30vw]" />
    </Tabs.TabPane>
  </Tabs>
);

const Payments = () => {
  const [tableData, setTableData] = useState<Payment[]>([]);
  const [total, setTotal] = useState(0);
  const [patientSearchLoading, setPatientSearchLoading] = useState(false);
  const [invoiceSearchLoading, setInvoiceSearchLoading] = useState(false);
  const { themeColor } = useGlobalConfig();

  const [tableParams, setTableParams] = useState<{
    invoice_number: string;
    patient_first_name: string;
    patient_last_name: string;
    date_of_birth: string;
    npi: string;
    pagination: {
      current: number;
      pageSize: number;
    };
  }>({
    invoice_number: '',
    patient_first_name: '',
    patient_last_name: '',
    date_of_birth: '',
    npi: '',
    pagination: {
      current: 1,
      pageSize: 25,
    },
  });

  const {
    data: dataInvoice,
    isLoading: isLoadingInvoice,
    refetch: refetchInvoice,
  } = useQueryPaymentsByInvoice({
    invoiceNumber: tableParams.invoice_number,
    page: tableParams.pagination.current,
  });

  const {
    data: dataPatient,
    isLoading: isLoadingPatient,
    refetch: refetchPatient,
  } = useQueryPaymentsByPatient({
    patientFirstName: tableParams.patient_first_name,
    patientLastName: tableParams.patient_last_name,
    dateOfBirth: dayjs(tableParams.date_of_birth).format('YYYY-MM-DD'),
    npi: tableParams.npi,
    page: tableParams.pagination.current,
  });

  const { mutate: downloadPayments, isPending: isDownloading } = useDownloadPayments();

  const handleSearchByInvoice = async () => {
    setInvoiceSearchLoading(true);
    setTableParams({
      ...tableParams,
      pagination: {
        current: 1,
        pageSize: 25,
      },
    });
    await refetchInvoice();
    setInvoiceSearchLoading(false);
  };

  const handleSearchByPatient = async () => {
    setPatientSearchLoading(true);
    setTableParams({
      ...tableParams,
      pagination: {
        current: 1,
        pageSize: 25,
      },
    });
    await refetchPatient();
    setPatientSearchLoading(false);
  };

  const handleTableChange = (pagination: any, _: any, sorter: any) => {
    setTableParams({
      ...tableParams,
      pagination: {
        current: pagination.current,
        pageSize: pagination.pageSize || tableParams.pagination.pageSize,
      },
      ...sorter,
    });
  };

  const handleDownload = () => {
    const params = {
      invoiceNumber: tableParams.invoice_number,
      patientFirstName: tableParams.patient_first_name,
      patientLastName: tableParams.patient_last_name,
      dateOfBirth: tableParams.date_of_birth
        ? dayjs(tableParams.date_of_birth).format('YYYY-MM-DD')
        : undefined,
      npi: tableParams.npi,
      total: total,
    };
    downloadPayments(params);
  };

  useEffect(() => {
    if (dataInvoice) {
      setTableData(dataInvoice?.data || []);
      setTotal(dataInvoice?.pagination?.total || 0);
    }

    if (dataPatient) {
      setTableData(dataPatient?.data || []);
      setTotal(dataPatient?.pagination?.total || 0);
    }
  }, [dataInvoice, dataPatient]);

  return (
    <>
      <Title level={4}>Search Payments</Title>
      <div className="mb-4">
        <span className="text-md font-bold text-gray-500">Search by Patient Info</span>
      </div>
      <div className="flex flex-col md:flex-row gap-y-2 md:gap-x-2">
        <div className="w-full md:w-[200px]">
          <Input
            placeholder="Patient First Name"
            value={tableParams.patient_first_name}
            onChange={(e) =>
              setTableParams({
                ...tableParams,
                patient_first_name: e.target.value,
                invoice_number: '',
                pagination: {
                  current: 1,
                  pageSize: 25,
                },
              })
            }
          />
        </div>
        <div className="w-full md:w-[200px]">
          <Input
            placeholder="Patient Last Name"
            value={tableParams.patient_last_name}
            onChange={(e) =>
              setTableParams({
                ...tableParams,
                patient_last_name: e.target.value,
                invoice_number: '',
                pagination: {
                  current: 1,
                  pageSize: 25,
                },
              })
            }
          />
        </div>
        <div className="w-full md:w-[200px]">
          <DatePicker
            style={{ width: '100%' }}
            placeholder="DOB (MM-DD-YYYY)"
            format="MM-DD-YYYY"
            value={
              tableParams.date_of_birth ? dayjs(tableParams.date_of_birth, 'MM-DD-YYYY') : null
            }
            onChange={(e) =>
              setTableParams({
                ...tableParams,
                date_of_birth: e ? dayjs(e).format('MM-DD-YYYY') : '',
                invoice_number: '',
                pagination: {
                  current: 1,
                  pageSize: 25,
                },
              })
            }
          />
        </div>
        <div className="w-full md:w-[200px]">
          <Input
            placeholder="NPI"
            value={tableParams.npi}
            onChange={(e) =>
              setTableParams({
                ...tableParams,
                npi: e.target.value,
                invoice_number: '',
                pagination: {
                  current: 1,
                  pageSize: 25,
                },
              })
            }
          />
        </div>
        <div className="w-full md:w-[200px]">
          <Button
            data-testid="patient-search-button"
            loading={isLoadingPatient || patientSearchLoading}
            type="primary"
            disabled={isLoadingPatient || patientSearchLoading}
            style={{ backgroundColor: themeColor, borderColor: themeColor }}
            onClick={handleSearchByPatient}
          >
            Search
          </Button>
        </div>
      </div>
      <div className="my-4 flex items-center space-x-2">
        <span className="text-md font-bold text-gray-500">Search by Invoice Number</span>
        <Popover trigger={['click']} content={<InvoiceNumberPopoverContent />}>
          <span className="underline text-xs">Where can I find my invoice number?</span>
        </Popover>
      </div>
      <div className="flex justify-between items-center">
        <Row gutter={24} className="grow">
          <Col xs={12} md={6}>
            <Input
              placeholder="Invoice Number"
              value={tableParams.invoice_number}
              onChange={(e) =>
                setTableParams({
                  ...tableParams,
                  invoice_number: e.target.value,
                  patient_first_name: '',
                  patient_last_name: '',
                  date_of_birth: '',
                  npi: '',
                })
              }
            />
          </Col>
          <Col span={6}>
            <Button
              data-testid="invoice-search-button"
              loading={isLoadingInvoice || invoiceSearchLoading}
              type="primary"
              disabled={isLoadingInvoice || invoiceSearchLoading}
              style={{ backgroundColor: themeColor, borderColor: themeColor }}
              onClick={handleSearchByInvoice}
            >
              Search
            </Button>
          </Col>
        </Row>
        {tableData.length > 0 && (
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            loading={isDownloading}
            disabled={isDownloading}
          >
            Export as CSV
          </Button>
        )}
      </div>
      <Table
        loading={
          isLoadingPatient || isLoadingInvoice || patientSearchLoading || invoiceSearchLoading
        }
        rowKey="id"
        bordered
        className="mt-4"
        pagination={{
          ...tableParams.pagination,
          showSizeChanger: false,
          total: total || 0,
        }}
        scroll={{
          x: 'max-content',
        }}
        onChange={handleTableChange}
        dataSource={tableData || []}
        columns={columns}
      />
    </>
  );
};

export default Payments;
